import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Label } from "../shadcn/ui/label";
import { Input } from "../shadcn/ui/input";
import { Switch } from "../shadcn/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../shadcn/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../shadcn/ui/tooltip";
import { Users, Star, Settings, Plus, Minus, Sparkles, Info, Clock } from "lucide-react";
import { useAmenities } from "../hooks/useAmenities";
import { useCurrencies } from "../hooks/useCurrencies";
import billingService from "../services/billingService";
import subcategoryFilterService from "../services/subcategoryFilterService";
import SubcategoryFiltersPanel from "./SubcategoryFiltersPanel";
import { DualRangeSlider } from "./DualRangeSlider";
import toastUtils from "../utils/toast";

// Illustrative Amenity Button Component (Chip Style) - Airbnb design
const IllustrativeAmenityButton = ({ label, selected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 rounded-full transition-colors duration-500
        ${
          selected
            ? "ring-1 ring-gray-900 text-black border-transparent"
            : "ring-1 ring-gray-200 text-black  border-transparent"
        }
      `}
    >
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
};

// Property Type Button Component - Airbnb design with shake animation and individual info icons
const PropertyTypeButton = ({ label, selected, onClick, disabled = false, disabledReason = "" }) => {
  const [isShaking, setIsShaking] = useState(false);

  const handleClick = e => {
    if (disabled) {
      // Shake animation when clicking disabled button
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);

      // Show toast when clicking disabled button
      toastUtils.info("Filter Disabled", {
        description: disabledReason,
      });

      e.preventDefault();
      return;
    }
    onClick();
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={handleClick}
        className={`px-3 py-2 text-sm font-medium transition-all duration-200 ${
          disabled
            ? "bg-gray-100 text-gray-400 ring-1 ring-gray-200 cursor-not-allowed"
            : selected
              ? "ring-1 ring-gray-900 text-black"
              : "ring-1 ring-gray-200 text-black"
        } ${isShaking ? "animate-shake" : ""}`}
      >
        {label}
      </button>
      {/* Individual info icon for each disabled button */}
      {disabled && disabledReason && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="p-1 -m-1">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{disabledReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

// Capacity Counter Component
const CapacityCounter = ({ value, onChange, min = 1, max = 100 }) => {
  const decrement = () => onChange(Math.max(min, (Number(value) || min) - 1));
  const increment = () => onChange(Math.min(max, (Number(value) || min) + 1));

  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={decrement}
        disabled={value <= min}
        className="h-7 w-7 rounded-full p-0"
      >
        <Minus className="w-3 h-3" />
      </Button>
      <div className="min-w-[40px] text-center text-sm font-medium tabular-nums">{value}</div>
      <Button
        type="button"
        variant="outline"
        onClick={increment}
        disabled={value >= max}
        className="h-7 w-7 rounded-full p-0"
      >
        <Plus className="w-3 h-3" />
      </Button>
    </div>
  );
};

const IllustrativeFilterDialog = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  initialFilters = {},
  totalResults: _totalResults = 0,
  showPublishedStatusFilter = false, // Only show on Properties.jsx
  categories = [],
  subcategories = [],
  isAuthenticated = false,
}) => {
  const [lazySubcategories, setLazySubcategories] = useState([]);
  const [loadingSubcategoryId, setLoadingSubcategoryId] = useState(null);
  const loadedCategoryIds = useRef(new Set());

  // Mark subcategories from props as already loaded
  useEffect(() => {
    subcategories.forEach(s => {
      if (s.categoryId) loadedCategoryIds.current.add(s.categoryId);
    });
  }, [subcategories]);

  // Merge pre-loaded and lazy-loaded subcategories, grouped by categoryId
  const subcategoriesByCategory = useMemo(() => {
    const map = {};
    [...subcategories, ...lazySubcategories].forEach(sub => {
      const catId = sub.categoryId;
      if (!catId) return;
      if (!map[catId]) map[catId] = [];
      if (!map[catId].some(e => e.id === sub.id)) map[catId].push(sub);
    });
    categories.forEach(cat => {
      const name = (cat.name || '').toUpperCase();
      const code = (cat.code || '').toUpperCase();
      if ((code === 'MOVE' || name.includes('MOVE')) && (!map[cat.id] || map[cat.id].length === 0)) {
        map[cat.id] = [
          { id: 201, name: "Book a Ride", code: "STANDARD_RIDE", categoryId: cat.id },
          { id: 202, name: "Rent a Vehicle", code: "CAR_RENTAL", categoryId: cat.id },
          { id: 203, name: "Chauffeur Service", code: "CHAUFFEUR", categoryId: cat.id },
          { id: 204, name: "Fleet Subscription", code: "FLEET", categoryId: cat.id },
        ];
      }
    });
    return map;
  }, [subcategories, lazySubcategories, categories]);

  const handleCategoryClick = useCallback(
    async categoryId => {
      if (loadedCategoryIds.current.has(categoryId)) return;
      loadedCategoryIds.current.add(categoryId);
      setLoadingSubcategoryId(categoryId);
      try {
        let data = [];

        // Try the all-in-one endpoint first
        if (isAuthenticated) {
          try {
            const response = await billingService.getAllServiceCategories({ type: "END_USER" });
            if (response?.data?.data) {
              const category = response.data.data.find(c => c.id === categoryId);
              if (category?.subcategories) {
                data = category.subcategories;
              }
            }
          } catch {
            // Fallback to per-category endpoint
          }
        }

        if (data.length === 0) {
          const response = isAuthenticated
            ? await billingService.getServiceSubcategories(categoryId)
            : await billingService.getPublicServiceSubcategories(categoryId);
          if (Array.isArray(response.data)) data = response.data;
          else if (Array.isArray(response.data?.content)) data = response.data.content;
          else if (Array.isArray(response.data?.data)) data = response.data.data;
        }

        setLazySubcategories(prev => [...prev, ...data.map(s => ({ ...s, categoryId }))]);
      } catch {
        // ignore
      } finally {
        setLoadingSubcategoryId(null);
      }
    },
    [isAuthenticated]
  );

  // Amenities hook with Redux caching
  const { amenities, amenitiesLoading, amenitiesError, clearAmenitiesError } = useAmenities();

  // Currencies hook with Redux caching
  const { currencies } = useCurrencies();

  // Get user's currency from localStorage
  const getUserCurrency = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.currency || "USD";
    } catch (e) {
      return "USD";
    }
  };

  // Get user's currency symbol
  const userCurrencyCode = getUserCurrency();
  const userCurrency = currencies.find(c => c.code === userCurrencyCode) ||
    currencies.find(c => c.code === "KES") ||
    currencies[0] || { symbol: "KES", code: "KES" };
  const currencySymbol = userCurrency.symbol || "KES";

  // Get 4 recommended amenities from the actual amenities data
  const getRecommendedAmenities = () => {
    if (!amenities || amenities.length === 0) return [];

    // Look for popular amenities by name (case-insensitive)
    const popularAmenityNames = [
      "wifi",
      "security",
      "parking",
      "air conditioning",
      "wifi",
      "free wifi",
      "24 hour security",
      "parking",
      "ac",
    ];

    const recommendedAmenities = [];

    // Try to find amenities that match popular names
    for (const name of popularAmenityNames) {
      const found = amenities.find(amenity => amenity.label?.toLowerCase().includes(name.toLowerCase()));
      if (found && !recommendedAmenities.find(rec => rec.id === found.id)) {
        recommendedAmenities.push(found);
        if (recommendedAmenities.length >= 4) break;
      }
    }

    // If we don't have 4, fill with random amenities
    if (recommendedAmenities.length < 4) {
      const remaining = amenities.filter(amenity => !recommendedAmenities.find(rec => rec.id === amenity.id));
      const needed = 4 - recommendedAmenities.length;
      const randomAmenities = remaining.sort(() => 0.5 - Math.random()).slice(0, needed);
      recommendedAmenities.push(...randomAmenities);
    }

    return recommendedAmenities.slice(0, 4);
  };

  const recommendedAmenities = getRecommendedAmenities();

  // Use EXACT same filter structure as FilterProperties.jsx (lines 117-129)
  const [filterState, setFilterState] = useState({
    priceRange: initialFilters.priceRange || [0, 10000],
    capacity: initialFilters.capacity || 1,
    amenities: initialFilters.amenities || [],
    publishedStatus: initialFilters.publishedStatus || [],
    featuredStatus: initialFilters.featuredStatus || [],
    topSpaces: initialFilters.topSpaces || false,
    duration: initialFilters.duration || [],
    categoryId: initialFilters.categoryId || null,
    subCategoryId: initialFilters.subCategoryId || null,
    filterOptionIds: initialFilters.filterOptionIds || [],
  });

  // Subcategory filters (Fulfillment & Vehicle Options)
  const [subcategoryFilters, setSubcategoryFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  useEffect(() => {
    if (!filterState.subCategoryId) {
      setSubcategoryFilters([]);
      return;
    }
    const numId = Number(filterState.subCategoryId);
    if (isNaN(numId)) return;

    setFiltersLoading(true);
    subcategoryFilterService.getSubcategoryFilters(numId)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || res.data?.content || [];
        setSubcategoryFilters(raw);
      })
      .catch(() => setSubcategoryFilters([]))
      .finally(() => setFiltersLoading(false));
  }, [filterState.subCategoryId]);

  // State for fade animation when featured section is disabled
  const [featuredSectionFading, setFeaturedSectionFading] = useState(false);

  // Watch for unpublished status changes to trigger fade animation
  useEffect(() => {
    if (filterState.publishedStatus.includes("UNPUBLISHED")) {
      setFeaturedSectionFading(true);
    } else {
      setFeaturedSectionFading(false);
    }
  }, [filterState.publishedStatus]);

  const handleApplyFilters = () => {
    onApplyFilters(filterState);
    onClose();
  };

  const handleClearFilters = () => {
    const resetFilters = {
      priceRange: [0, 10000],
      capacity: 1,
      amenities: [],
      publishedStatus: [],
      featuredStatus: [],
      topSpaces: false,
      duration: [],
      categoryId: null,
      subCategoryId: null,
      filterOptionIds: [],
    };
    setFilterState(resetFilters);
    onClearFilters();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-4 pb-3 flex-shrink-0">
          <DialogTitle className="text-lg font-semibold text-gray-900">Filters</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-3 py-2">
            {/* Recommended for You Section - HIDDEN */}
            {/* <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  Recommended for You
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-1">
                  <p className="text-xs text-gray-600 mb-2">Most requested amenities</p>
                  {amenitiesLoading ? (
                    <div className="flex items-center justify-center py-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-xs text-gray-500">Loading recommended amenities...</span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {recommendedAmenities.map(amenity => (
                        <IllustrativeAmenityButton
                          key={amenity.id}
                          label={amenity.label}
                          selected={filterState.amenities.includes(amenity.id)}
                          onClick={() => {
                            if (filterState.amenities.includes(amenity.id)) {
                              setFilterState(prev => ({
                                ...prev,
                                amenities: prev.amenities.filter(id => id !== amenity.id),
                              }));
                            } else {
                              setFilterState(prev => ({
                                ...prev,
                                amenities: [...prev.amenities, amenity.id],
                              }));
                            }
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card> */}

            {/* Category Section */}
            <Card className="border-gray-200 shadow-none">
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  Category
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-2">
                  {categories
                    .filter(cat => cat.id)
                    .map(category => (
                      <PropertyTypeButton
                        key={category.id}
                        label={category.name}
                        selected={filterState.categoryId === category.id}
                        onClick={() => {
                          const newId = filterState.categoryId === category.id ? null : category.id;
                          setFilterState(prev => ({
                            ...prev,
                            categoryId: newId,
                            subCategoryId: newId === null ? null : prev.subCategoryId,
                          }));
                          if (newId) handleCategoryClick(newId);
                        }}
                      />
                    ))}
                  {categories.length === 0 && <p className="text-xs text-gray-400 py-1">Loading categories...</p>}
                </div>

                {/* Subcategories - animated expand when a category is selected */}
                <AnimatePresence>
                  {filterState.categoryId && (
                    <motion.div
                      key={filterState.categoryId}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      className="mt-3 pt-3 border-t border-gray-100 overflow-hidden"
                    >
                      {loadingSubcategoryId === filterState.categoryId ? (
                        <div className="flex items-center gap-2 py-1">
                          <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                          <span className="text-xs text-gray-400">Loading subcategories...</span>
                        </div>
                      ) : subcategoriesByCategory[filterState.categoryId]?.length > 0 ? (
                        <>
                          <Label className="text-xs font-medium text-gray-500 mb-2 block">Subcategory</Label>
                          <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={{
                              visible: { transition: { staggerChildren: 0.03 } },
                            }}
                            className="flex flex-wrap gap-1.5"
                          >
                            <motion.button
                              variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } }}
                              onClick={() => setFilterState(prev => ({ ...prev, subCategoryId: null }))}
                              className={`text-xs px-2.5 py-1 rounded-full transition-all ${
                                !filterState.subCategoryId
                                  ? "bg-gray-900 text-white"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                            >
                              All
                            </motion.button>
                            {subcategoriesByCategory[filterState.categoryId].map(sub => (
                              <motion.button
                                key={sub.id}
                                variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } }}
                                onClick={() =>
                                  setFilterState(prev => ({
                                    ...prev,
                                    subCategoryId: prev.subCategoryId === sub.id ? null : sub.id,
                                    filterOptionIds: [],
                                  }))
                                }
                                className={`text-xs px-2.5 py-1 rounded-full transition-all ${
                                  filterState.subCategoryId === sub.id
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                              >
                                {sub.name || sub.code}
                              </motion.button>
                            ))}
                          </motion.div>
                        </>
                      ) : null}

                      {filterState.subCategoryId && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <Label className="text-xs font-semibold text-gray-700 mb-2 block">
                            Fulfillment & Options
                          </Label>
                          <SubcategoryFiltersPanel
                            filters={subcategoryFilters}
                            selectedOptionIds={filterState.filterOptionIds || []}
                            onToggleOption={(optId) => {
                              setFilterState(prev => {
                                const current = prev.filterOptionIds || [];
                                const next = current.includes(optId)
                                  ? current.filter(id => id !== optId)
                                  : [...current, optId];
                                return { ...prev, filterOptionIds: next };
                              });
                            }}
                            isLoading={filtersLoading}
                            isError={false}
                          />
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Status & Visibility Section - Only show when showPublishedStatusFilter is true (Properties.jsx) */}
            {showPublishedStatusFilter && (
              <Card>
                <CardHeader className="p-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Star className="w-3 h-3" />
                    Status & Visibility
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 p-3">
                  <div>
                    <Label className="text-xs font-medium text-gray-700 mb-1 block">Publication Status</Label>
                    <div className="flex flex-wrap gap-3">
                      <PropertyTypeButton
                        label="Published"
                        selected={filterState.publishedStatus.includes("PUBLISHED")}
                        onClick={() => {
                          if (filterState.publishedStatus.includes("PUBLISHED")) {
                            setFilterState(prev => ({ ...prev, publishedStatus: [] }));
                          } else {
                            setFilterState(prev => ({ ...prev, publishedStatus: ["PUBLISHED"] }));
                          }
                        }}
                      />
                      <PropertyTypeButton
                        label="Unpublished"
                        selected={filterState.publishedStatus.includes("UNPUBLISHED")}
                        onClick={() => {
                          if (filterState.publishedStatus.includes("UNPUBLISHED")) {
                            setFilterState(prev => ({ ...prev, publishedStatus: [] }));
                          } else {
                            setFilterState(prev => ({
                              ...prev,
                              publishedStatus: ["UNPUBLISHED"],
                              featuredStatus: [],
                            }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div
                    className={`mt-2 transition-all duration-300 ${featuredSectionFading ? "opacity-50" : "opacity-100"}`}
                  >
                    <div className="mb-2">
                      <Label className="text-sm font-medium text-gray-700">Featured Status</Label>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <PropertyTypeButton
                        label="Featured"
                        selected={filterState.featuredStatus.includes("FEATURED")}
                        disabled={filterState.publishedStatus.includes("UNPUBLISHED")}
                        disabledReason="Featured spaces are only available for published properties"
                        onClick={() => {
                          if (filterState.publishedStatus.includes("UNPUBLISHED")) return;
                          if (filterState.featuredStatus.includes("FEATURED")) {
                            setFilterState(prev => ({ ...prev, featuredStatus: [] }));
                          } else {
                            setFilterState(prev => ({ ...prev, featuredStatus: ["FEATURED"] }));
                          }
                        }}
                      />
                      <PropertyTypeButton
                        label="Not Featured"
                        selected={filterState.featuredStatus.includes("NOT_FEATURED")}
                        disabled={filterState.publishedStatus.includes("UNPUBLISHED")}
                        disabledReason="Featured status filtering is only available for published properties"
                        onClick={() => {
                          if (filterState.publishedStatus.includes("UNPUBLISHED")) return;
                          if (filterState.featuredStatus.includes("NOT_FEATURED")) {
                            setFilterState(prev => ({ ...prev, featuredStatus: [] }));
                          } else {
                            setFilterState(prev => ({ ...prev, featuredStatus: ["NOT_FEATURED"] }));
                          }
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Duration Section */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Booking Duration
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Duration Type</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "HOURLY", label: "per hour" },
                      { value: "DAILY", label: "per day" },
                      { value: "WEEKLY", label: "per week" },
                      { value: "MONTHLY", label: "per month" },
                    ].map(duration => (
                      <PropertyTypeButton
                        key={duration.value}
                        label={duration.label}
                        selected={filterState.duration.includes(duration.value)}
                        onClick={() => {
                          if (filterState.duration.includes(duration.value)) {
                            setFilterState(prev => ({
                              ...prev,
                              duration: prev.duration.filter(d => d !== duration.value),
                            }));
                          } else {
                            setFilterState(prev => ({
                              ...prev,
                              duration: [...prev.duration, duration.value],
                            }));
                          }
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Filter spaces by their available booking durations</p>
                </div>
              </CardContent>
            </Card>

            {/* Price & Capacity Section */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">Price & Capacity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 p-3">
                {/* Price Range */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Price Range</Label>
                  <div className="px-4">
                    <DualRangeSlider
                      value={filterState.priceRange}
                      onValueChange={value => setFilterState(prev => ({ ...prev, priceRange: value }))}
                      max={10000}
                      min={0}
                      step={100}
                      className="w-full"
                    />
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-center gap-3">
                      <div className="flex items-center gap-1">
                        <Label htmlFor="min-price" className="text-xs text-gray-600">
                          KES
                        </Label>
                        <Input
                          id="min-price"
                          type="number"
                          value={filterState.priceRange[0]}
                          onChange={e => {
                            const value = parseInt(e.target.value) || 0;
                            setFilterState(prev => ({
                              ...prev,
                              priceRange: [Math.max(0, value), prev.priceRange[1]],
                            }));
                          }}
                          className="w-16 text-center text-sm"
                        />
                      </div>
                      <span className="text-xs text-gray-400">to</span>
                      <div className="flex items-center gap-1">
                        <Label htmlFor="max-price" className="text-xs text-gray-600">
                          KES
                        </Label>
                        <Input
                          id="max-price"
                          type="number"
                          value={filterState.priceRange[1]}
                          onChange={e => {
                            const value = parseInt(e.target.value) || 10000;
                            setFilterState(prev => ({
                              ...prev,
                              priceRange: [prev.priceRange[0], Math.min(10000, value)],
                            }));
                          }}
                          className="w-16 text-center text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Capacity */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 flex items-center gap-2">
                    <Users className="w-3 h-3" />
                    Capacity
                  </Label>
                  <div className="px-2">
                    <CapacityCounter
                      value={filterState.capacity}
                      onChange={val => setFilterState(prev => ({ ...prev, capacity: val }))}
                      min={1}
                      max={100}
                    />
                    <div className="text-center text-xs text-gray-600 mt-1">{filterState.capacity} people</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Amenities Section - HIDDEN */}
            {/* <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings className="w-3 h-3" />
                  Amenities
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-1">
                  {amenitiesLoading && (
                    <div className="flex items-center justify-center py-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-xs text-gray-500">Loading amenities...</span>
                    </div>
                  )}
                  {!amenitiesLoading && (
                    <div className="flex flex-wrap gap-2">
                      {amenities.map(amenity => (
                        <IllustrativeAmenityButton
                          key={amenity.id}
                          label={amenity.label}
                          selected={filterState.amenities.includes(amenity.id)}
                          onClick={() => {
                            if (filterState.amenities.includes(amenity.id)) {
                              setFilterState(prev => ({
                                ...prev,
                                amenities: prev.amenities.filter(id => id !== amenity.id),
                              }));
                            } else {
                              setFilterState(prev => ({
                                ...prev,
                                amenities: [...prev.amenities, amenity.id],
                              }));
                            }
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {amenitiesError && (
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-red-600">Failed to load amenities</p>
                      <button
                        onClick={clearAmenitiesError}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card> */}

            {/* Additional Options */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="text-sm">Additional Options</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
                  <div>
                    <Label className="text-xs font-medium text-gray-700">Top Spaces</Label>
                    <p className="text-xs text-gray-500">Show only highly rated spaces</p>
                  </div>
                  <Switch
                    checked={filterState.topSpaces}
                    onCheckedChange={checked => setFilterState(prev => ({ ...prev, topSpaces: checked }))}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="flex justify-between px-6 py-4 bg-white border-t border-gray-200 flex-shrink-0">
          <Button variant="outline" onClick={handleClearFilters} className="px-6">
            Clear all
          </Button>
          <Button onClick={handleApplyFilters} className="px-6 bg-gray-900 hover:bg-gray-800">
            Show spaces
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IllustrativeFilterDialog;

// Add custom CSS animations for shake effect only
const style = document.createElement("style");
style.textContent = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
  }

  .animate-shake {
    animation: shake 0.5s;
  }
`;
if (typeof document !== "undefined" && !document.getElementById("filter-dialog-animations")) {
  style.id = "filter-dialog-animations";
  document.head.appendChild(style);
}
